import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  HeartPulse, Phone, MapPin, User, ShieldCheck, Building2, Loader2,
  ChevronRight, CheckCircle2, AlertTriangle, Circle,
} from "lucide-react";

const ICONS = { phone: Phone, "map-pin": MapPin, user: User, shield: ShieldCheck, building: Building2 };
const SEV = {
  urgent: { dot: "bg-red-500", chip: "bg-red-50 text-red-700 border-red-200" },
  warning: { dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700 border-amber-200" },
  info: { dot: "bg-slate-400", chip: "bg-slate-50 text-slate-600 border-slate-200" },
};

export default function SetupHealth() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [open, setOpen] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await client.get("/setup-health");
      setData(res.data);
      const first = (res.data.groups || []).find((g) => g.count > 0);
      if (first) setOpen({ [first.key]: true });
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
    }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="setup-health-loading">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  if (denied) {
    return (
      <Card className="p-8 border-slate-200 max-w-lg mx-auto text-center space-y-2" data-testid="setup-health-denied">
        <ShieldCheck className="h-8 w-8 text-slate-400 mx-auto" />
        <p className="font-semibold text-slate-900">Owners &amp; admins only</p>
        <p className="text-sm text-slate-500">You don’t have access to the Setup Health panel.</p>
      </Card>
    );
  }

  const groups = data?.groups || [];
  const total = data?.totalIssues || 0;

  return (
    <div className="space-y-6" data-testid="setup-health-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
            <HeartPulse className="h-6 w-6 text-brand" /> Setup Health
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Data gaps that need attention across your {data?.staffCount || 0} staff and {data?.siteCount || 0} sites.
          </p>
        </div>
        <div
          data-testid="setup-health-total"
          className={`rounded-xl px-4 py-2 text-center border ${total === 0 ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}
        >
          <div className="text-2xl font-bold leading-none">{total}</div>
          <div className="text-[11px] font-medium uppercase tracking-wide">open items</div>
        </div>
      </div>

      {total === 0 && (
        <Card className="p-8 border-emerald-200 bg-emerald-50/40 flex items-center gap-3" data-testid="setup-health-clear">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          <div>
            <p className="font-semibold text-slate-900">Everything looks set up.</p>
            <p className="text-sm text-slate-600">No missing phone numbers, sites, managers or geofences right now.</p>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {groups.map((g) => {
          const Icon = ICONS[g.icon] || Circle;
          const sev = SEV[g.severity] || SEV.info;
          const clear = g.count === 0;
          const isOpen = !!open[g.key];
          return (
            <Card key={g.key} className="border-slate-200 overflow-hidden" data-testid={`health-group-${g.key}`}>
              <button
                data-testid={`health-toggle-${g.key}`}
                onClick={() => !clear && setOpen((o) => ({ ...o, [g.key]: !o[g.key] }))}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left ${clear ? "cursor-default" : "hover:bg-slate-50"}`}
              >
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${clear ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-600"}`}>
                  {clear ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900 text-sm">{g.label}</span>
                    {!clear && (
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${sev.chip}`}>{g.count}</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{g.hint}</p>
                </div>
                {!clear && (
                  <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                )}
              </button>
              {!clear && isOpen && (
                <div className="border-t border-slate-100 divide-y divide-slate-50">
                  {g.items.map((it) => (
                    <button
                      key={`${g.key}-${it.id}`}
                      data-testid={`health-item-${g.key}-${it.id}`}
                      onClick={() => navigate(it.link)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 pl-16 text-left hover:bg-brand/5 group"
                    >
                      <span className={`h-2 w-2 rounded-full flex-shrink-0 ${sev.dot}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{it.name}</p>
                        {it.sub && <p className="text-xs text-slate-500 truncate">{it.sub}</p>}
                      </div>
                      <span className="text-xs text-brand opacity-0 group-hover:opacity-100 flex items-center gap-1">
                        Fix <ChevronRight className="h-3 w-3" />
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <p className="text-xs text-slate-400 flex items-center gap-1.5">
        <AlertTriangle className="h-3 w-3" /> Tap any row to open the staff or site record and fill in the missing detail.
      </p>
    </div>
  );
}
